import React, { useState, useEffect } from 'react';
import ModalPortal from '../UI/ModalPortal';
import { X, AlertCircle, CheckCircle, Lock, MessageSquare, CalendarDays } from 'lucide-react';
import { talents, jobApplications } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import { TextField, DatePicker } from '@anyemp/ui-kit';
import { formatApiError } from '../../utils/formatApiError';

/** Must match Joi statusUpdateSchema.reason.max in src/middleware/validation.js */
const STATUS_REASON_MAX_LENGTH = 500;

function formatInlineApiError(data, fallback) {
  if (!data || typeof data !== 'object') return fallback;
  return formatApiError({ response: { data } }, fallback);
}

const StatusTransitionModal = ({
  isOpen,
  onClose,
  entityType = 'talent', // 'talent' or 'job_application'
  entityId,
  entityName,
  fromStatus,
  toStatus,
  onSuccess,
  onError
}) => {
  const [reason, setReason] = useState('');
  const [transitionInfo, setTransitionInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('check'); // 'check', 'reason', 'dates', 'success', 'error'

  // Перевод в Fired/Left требует даты ухода, а она — даты Start Work.
  // Какие именно поля нужны, сообщает сервер в required_fields
  const [requiredDateFields, setRequiredDateFields] = useState([]);
  const [departureDate, setDepartureDate] = useState('');
  const [startWorkDate, setStartWorkDate] = useState('');
  const needsDepartureDate = requiredDateFields.includes('departure_date');
  const needsStartWorkDate = requiredDateFields.includes('start_work_date');

  const resetTransitionState = () => {
    setStep('check');
    setReason('');
    setError(null);
    setTransitionInfo(null);
    setRequiredDateFields([]);
    setDepartureDate('');
    setStartWorkDate('');
  };

  useEffect(() => {
    if (!isOpen) resetTransitionState();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      checkTransitionRequirements();
    }
  }, [isOpen, fromStatus, toStatus]);

  const checkTransitionRequirements = async () => {
    if (!fromStatus || !toStatus) {
      setError('Missing status information');
      setStep('error');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStep('check');

      // Get transition information
      const response = await talents.getStatusTransition(fromStatus.id, toStatus.id);
      
      if (response.success) {
        setTransitionInfo(response.data);

        // Check if reason is required for this transition
        if (response.data.transition_reason_required) {
          // Reason required - show reason input step
          setStep('reason');
        } else {
          // Reason not required - execute transition immediately
          executeTransition();
        }
      } else {
        // Check if error is about required reason
        const errorDetails = Array.isArray(response.details)
          ? response.details.join(' ')
          : (response.details || '');

        if (errorDetails.includes('Transition reason is required')) {
          // Reason is required - show reason input step
          setTransitionInfo({ transition_reason_required: true });
          setStep('reason');
        } else {
          setError(formatInlineApiError(response, 'Transition not allowed'));
          setStep('error');
        }
      }
    } catch (err) {
      console.error('Error checking transition:', err);

      // Handle errors from GET transition check
      if (err.response?.data) {
        setError(formatApiError(err, 'Error checking transition'));
        setStep('error');
      } else {
        setError(err.message || 'Error checking transition requirements');
        setStep('error');
      }
    } finally {
      setLoading(false);
    }
  };


  const executeTransition = async (providedReason = null) => {
    try {
      setLoading(true);
      setError(null);

      const statusData = {
        status_id: toStatus.id,
        reason: providedReason || reason || 'Status changed via UI',
        ...(departureDate && { departure_date: departureDate }),
        ...(startWorkDate && { start_work_date: startWorkDate })
      };

      // Call appropriate API based on entity type
      const response = entityType === 'job_application'
        ? await jobApplications.updateJobApplicationStatus(entityId, statusData.status_id)
        : await talents.updateTalentStatus(entityId, statusData);

      if (response && response.data) {
        setStep('success');
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        throw new Error('Unexpected server response');
      }
    } catch (err) {
      console.error('Error executing transition:', err);

      const d = err.response?.data;

      // Сервер сказал, каких дат не хватает — показываем поля вместо ошибки
      if (Array.isArray(d?.required_fields) && d.required_fields.length > 0) {
        setRequiredDateFields(d.required_fields);
        setError(d.message || 'Departure date is required');
        setStep('dates');
        setLoading(false);
        return;
      }

      const messageOnly = typeof d?.message === 'string' ? d.message : '';
      const detailsJoined = Array.isArray(d?.details) ? d.details.join(' ') : (typeof d?.details === 'string' ? d.details : '');
      const reasonRequiredHint =
        messageOnly.includes('Transition reason is required') ||
        detailsJoined.includes('Transition reason is required') ||
        messageOnly.includes('Reason is required') ||
        detailsJoined.includes('Reason is required');

      if (reasonRequiredHint) {
        setError('Transition reason is required');
        setStep('reason');
      } else {
        setError(formatApiError(err, `Error changing status: ${err.message || 'Unknown error'}`));
        setStep('error');
      }

      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReason = () => {
    // Перевіряємо reason тільки якщо він обов'язковий
    if (transitionInfo?.transition_reason_required && !reason.trim()) {
      setError('Please specify the reason for status change (required)');
      return;
    }
    executeTransition(reason);
  };

  const handleSubmitDates = () => {
    if (needsDepartureDate && !departureDate) {
      setError(`Please specify the "${toStatus?.name}" date`);
      return;
    }
    if (needsStartWorkDate && !startWorkDate) {
      setError('Please specify the "Start Work" date');
      return;
    }
    setError(null);
    executeTransition(reason);
  };

  const handleClose = () => {
    resetTransitionState();
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ModalPortal onClose={handleClose} ariaLabel="Status transition">
      <div className="mx-4 flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Status Change
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Entity Info */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {entityName}
            </h4>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: fromStatus?.color || '#6B7280' }}
                ></div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {fromStatus?.name}
                </span>
              </div>
              <span className="text-gray-400">→</span>
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: toStatus?.color || '#6B7280' }}
                ></div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {toStatus?.name}
                </span>
              </div>
            </div>
          </div>

          {/* Step Content */}
          {step === 'check' && (
            <div className="flex justify-center items-center py-8">
              <Loader variant="component" size="md" fullHeight={false} />
            </div>
          )}

          {step === 'reason' && (
            <div className="space-y-4">
              {transitionInfo?.transition_reason_required && (
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                      Status change reason required
                    </h5>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      Reason must be provided for this transition
                    </p>
                  </div>
                </div>
              )}

              <div>
                <TextField
                  label="Status Change Reason"
                  required={transitionInfo?.transition_reason_required}
                  multiline
                  rows={3}
                  fullWidth
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={STATUS_REASON_MAX_LENGTH}
                  placeholder={transitionInfo?.transition_reason_required
                    ? "Please specify the reason for status change (required)..."
                    : "Please specify the reason for status change (optional)..."}
                  autoFocus
                  helperText={`${reason.length}/${STATUS_REASON_MAX_LENGTH}`}
                />
              </div>

            </div>
          )}

          {step === 'dates' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <CalendarDays className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                    Departure date required
                  </h5>
                  <p className="text-xs text-blue-700 dark:text-blue-400">
                    {needsStartWorkDate
                      ? `This talent has no "Start Work" date yet — please fill in both dates to move them to "${toStatus?.name}".`
                      : `Please specify when the talent was moved to "${toStatus?.name}".`}
                  </p>
                </div>
              </div>

              {needsStartWorkDate && (
                <DatePicker
                  label="Start Work Date"
                  required
                  fullWidth
                  value={startWorkDate ? new Date(startWorkDate) : null}
                  onChange={(d) => setStartWorkDate(d ? d.toISOString().slice(0, 10) : '')}
                />
              )}

              {needsDepartureDate && (
                <DatePicker
                  label={`${toStatus?.name} Date`}
                  required
                  fullWidth
                  value={departureDate ? new Date(departureDate) : null}
                  onChange={(d) => setDepartureDate(d ? d.toISOString().slice(0, 10) : '')}
                />
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                A future date keeps the current status until that day arrives.
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Status Successfully Changed!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Transition completed according to requirements
              </p>
            </div>
          )}

          {step === 'error' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="text-sm font-medium text-red-900 dark:text-red-300 mb-1">
                    Transition Not Possible
                  </h5>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    {error}
                  </p>
                </div>
              </div>

              {transitionInfo && transitionInfo.requires_approval && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <Lock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <h6 className="text-xs font-medium text-amber-900 dark:text-amber-300">
                      Role approval required
                    </h6>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Available roles: {Array.isArray(transitionInfo.approval_role) 
                        ? transitionInfo.approval_role.join(', ') 
                        : transitionInfo.approval_role}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && step !== 'error' && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-700 sm:px-6">
          {step === 'check' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Cancel
            </button>
          )}

          {step === 'reason' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReason}
                disabled={(transitionInfo?.transition_reason_required && !reason.trim()) || loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing...' : 'Change Status'}
              </button>
            </>
          )}

          {step === 'dates' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDates}
                disabled={loading || (needsDepartureDate && !departureDate) || (needsStartWorkDate && !startWorkDate)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing...' : 'Change Status'}
              </button>
            </>
          )}

          {step === 'success' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              Close
            </button>
          )}

          {step === 'error' && (
            <>
              <button
                type="button"
                onClick={() => checkTransitionRequirements()}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
              >
                Try Again
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default StatusTransitionModal;
